import React, { useState, useEffect } from 'react';
import { type TeamMember, timetablesService } from '../../services/timetablesService';

interface ScheduleEditorProps {
  member: TeamMember;
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
}

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const HOURS = Array.from({ length: 24 }, (_, i) => i);

export const ScheduleEditor: React.FC<ScheduleEditorProps> = ({ member, isOpen, onClose, onSave }) => {
  const [schedule, setSchedule] = useState<Record<number, number[]>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (member && isOpen) {
      setSchedule(member.schedule || {});
    }
  }, [member, isOpen]);

  const toggleHour = (day: number, hour: number) => {
    setSchedule(prev => {
      const daySchedule = prev[day] || [];
      const newDaySchedule = daySchedule.includes(hour)
        ? daySchedule.filter(h => h !== hour)
        : [...daySchedule, hour].sort((a, b) => a - b);

      return {
        ...prev,
        [day]: newDaySchedule,
      };
    });
  };

  const isHourSelected = (day: number, hour: number) => {
    return (schedule[day] || []).includes(hour);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await timetablesService.updateSchedule(member.id, { schedule });
      onSave();
    } catch (error) {
      console.error('Failed to save schedule:', error);
    } finally {
      setSaving(false);
    }
  };

  const clearDay = (day: number) => {
    setSchedule(prev => ({ ...prev, [day]: [] }));
  };

  const fillDay = (day: number) => {
    setSchedule(prev => ({ ...prev, [day]: HOURS }));
  };

  const formatHour = (hour: number) => {
    if (hour === 0) return '12 AM';
    if (hour === 12) return '12 PM';
    if (hour < 12) return `${hour} AM`;
    return `${hour - 12} PM`;
  };

  const getCurrentTimeInTimezone = () => {
    try {
      const now = new Date();
      const timeString = now.toLocaleString('en-US', {
        timeZone: member.timezone,
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
      return timeString;
    } catch (error) {
      return 'Invalid timezone';
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className='fixed inset-0 flex items-center justify-center z-50'
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        backdropFilter: 'blur(4px)',
      }}
    >
      <div
        className='dashboard-card fade-in'
        style={{
          width: '95vw',
          maxWidth: '1200px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          margin: '20px',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div className='dashboard-card__header'>
          <div>
            <h2 className='dashboard-card__title' style={{ margin: '0 0 8px 0', fontSize: '1.25rem' }}>
              Schedule for {member.name}
            </h2>
            <div
              style={{
                fontSize: '0.875rem',
                color: 'var(--light-gray)',
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                flexWrap: 'wrap',
              }}
            >
              <span>
                <strong>Timezone:</strong> {member.timezone}
              </span>
              <span>
                <strong>Current time:</strong> {getCurrentTimeInTimezone()}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--light-gray)',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '4px',
              transition: 'color 0.15s ease',
            }}
            onMouseOver={e => ((e.target as HTMLButtonElement).style.color = 'var(--white-text)')}
            onMouseOut={e => ((e.target as HTMLButtonElement).style.color = 'var(--light-gray)')}
          >
            <svg width='24' height='24' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
            </svg>
          </button>
        </div>

        <div className='dashboard-card__content' style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
          {/* Instructions */}
          <div
            style={{
              marginBottom: '24px',
              padding: '12px 16px',
              backgroundColor: 'var(--tertiary-bg)',
              borderRadius: '4px',
              borderLeft: '3px solid var(--blue-accent)',
              fontSize: '0.875rem',
              color: 'var(--light-gray)',
            }}
          >
            <strong>Instructions:</strong> Click on time slots to toggle availability. All times are shown in {member.timezone}.
          </div>

          {/* Schedule Grid */}
          <div className='schedule-grid'>
            {/* Header row */}
            <div style={{ padding: '8px 4px' }}></div>
            {DAYS.map(day => (
              <div key={day} className='schedule-grid__header'>
                {day}
              </div>
            ))}

            {/* Hour rows */}
            {HOURS.map(hour => (
              <React.Fragment key={hour}>
                <div className={`schedule-grid__hour-label ${hour % 2 === 0 ? 'schedule-grid__hour-label--even' : ''}`}>
                  {formatHour(hour)}
                </div>
                {DAYS.map((_, dayIndex) => (
                  <div key={dayIndex} style={{ padding: '1px' }}>
                    <button
                      onClick={() => toggleHour(dayIndex, hour)}
                      className={`schedule-grid__time-slot ${isHourSelected(dayIndex, hour) ? 'schedule-grid__time-slot--selected' : ''}`}
                    >
                      {isHourSelected(dayIndex, hour) && <span className='schedule-grid__checkmark'>✓</span>}
                    </button>
                  </div>
                ))}
              </React.Fragment>
            ))}
          </div>

          {/* Day controls */}
          <div className='day-controls-grid'>
            {DAYS.map((day, index) => (
              <div key={index} className='day-control'>
                <span className='day-control__label'>{day}</span>
                <button onClick={() => fillDay(index)} className='day-control__button day-control__button--fill'>
                  Fill All
                </button>
                <button onClick={() => clearDay(index)} className='day-control__button day-control__button--clear'>
                  Clear All
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Footer with action buttons */}
        <div className='dashboard-card__footer'>
          <div style={{ fontSize: '0.75rem', color: 'var(--light-gray)' }}>{Object.values(schedule).flat().length} hour slots selected</div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={onClose}
              style={{
                padding: '8px 16px',
                fontSize: '0.875rem',
                fontWeight: '500',
                color: 'var(--light-gray)',
                backgroundColor: 'var(--tertiary-bg)',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                transition: 'background-color 0.15s ease',
              }}
              onMouseOver={e => ((e.target as HTMLButtonElement).style.backgroundColor = 'var(--divider-gray)')}
              onMouseOut={e => ((e.target as HTMLButtonElement).style.backgroundColor = 'var(--tertiary-bg)')}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                padding: '8px 16px',
                fontSize: '0.875rem',
                fontWeight: '500',
                color: 'var(--white-text)',
                backgroundColor: saving ? 'var(--medium-gray)' : 'var(--primary-red)',
                border: 'none',
                borderRadius: '4px',
                cursor: saving ? 'not-allowed' : 'pointer',
                transition: 'background-color 0.15s ease',
                opacity: saving ? 0.6 : 1,
              }}
              onMouseOver={e => {
                if (!saving) {
                  (e.target as HTMLButtonElement).style.backgroundColor = 'var(--dark-red)';
                }
              }}
              onMouseOut={e => {
                if (!saving) {
                  (e.target as HTMLButtonElement).style.backgroundColor = 'var(--primary-red)';
                }
              }}
            >
              {saving ? 'Saving...' : 'Save Schedule'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
